/* eslint-disable @typescript-eslint/no-unused-vars */
import { useState } from 'react';
import { EPerPages, ISortOptions } from 'models/table';
import { IPaginator } from 'models/paginator';
import {
  IBatchesResponseContent,
  IBatchesSearchResponse,
  ILicenceResponseContent,
  ILicencesSearchForm,
  IPrintoutsResponseContent,
  IPrintoutsSearchForm,
  IWeaponLicencePrintBatchDetails,
  IWeaponLicencePrintBatchHistory,
  IWeaponLicencePrintHistory,
} from 'models/licences';
import { EDictionaryTypes } from 'models/dictionary';
import { useDictionary } from 'models/dictionaryContext';
import {
  getBatches,
  getLicences,
  getPrintouts,
  getWeaponLicencePrint,
  getWeaponLicencePrintBatch,
  getWeaponLicencePrintBatchHistory,
  getWeaponLicencePrintHistory,
  searchWeaponLicencePrintBatchPrintouts,
} from 'requests/licences';

export const useLicences = () => {
  const [paginator, setPaginator] = useState<IPaginator | null>(null);
  const [licences, setLicences] = useState<ILicenceResponseContent[] | null | undefined>(null);
  const [printoutsPaginator, setPrintoutsPaginator] = useState<IPaginator | null>(null);
  const [batchesPaginator, setBatchesPaginator] = useState<IPaginator | null>(null);
  const [printouts, setPrintouts] = useState<IPrintoutsResponseContent[]>([]);
  const [batches, setBatches] = useState<IBatchesResponseContent[]>([]);
  const {
    getLegalType,
    legalType,
    getWeaponLicencingAuthority,
    weaponLicencingAuthority,
    getWeaponLicencePrintBatchStatus,
    weaponLicencePrintBatchStatus,
    getWeaponLicencePrintStatus,
    weaponLicencePrintStatus,
  } = useDictionary();
  const [batchDetails, setBatchDetails] = useState<IWeaponLicencePrintBatchDetails | null>(
    null,
  );
  const [batchPrintouts, setBatchPrintouts] = useState<IPrintoutsResponseContent[]>([]);
  const [batchPrintoutsPaginator, setBatchPrintoutsPaginator] = useState<IPaginator | null>(
    null,
  );
  const [batchHistory, setBatchHistory] = useState<IWeaponLicencePrintBatchHistory[]>([]);
  const [batchHistoryPaginator, setBatchHistoryPaginator] = useState<IPaginator | null>(null);
  const [printoutDetails, setPrintoutDetails] = useState<IPrintoutsResponseContent | null>(
    null,
  );
  const [printHistory, setPrintHistory] = useState<IWeaponLicencePrintHistory[]>([]);

  const getLicencesSearchData = async (
    currentPage: number = 1,
    perPage: EPerPages = EPerPages.perPage25,
    query: Partial<ILicencesSearchForm> = {},
    sort?: ISortOptions | null,
  ) => {
    const licencesData = await getLicences(currentPage, perPage, sort, query);

    if (licencesData) {
      const { content, ...paginatorData } = licencesData;
      if (content) {
        setLicences(content);
        setPaginator(paginatorData);
      } else {
        setLicences([]);
        setPaginator(null);
      }
    }
  };

  const getPrintoutsSearchData = async (
    currentPage: number = 1,
    perPage: EPerPages = EPerPages.perPage25,
    query: Partial<IPrintoutsSearchForm> = {},
    sort?: ISortOptions | null,
  ) => {
    const licencesData = await getPrintouts(currentPage, perPage, sort, query);

    if (licencesData) {
      const { content, ...paginatorData } = licencesData;
      if (content) {
        setPrintouts(content);
        setPrintoutsPaginator(paginatorData);
      } else {
        setPrintouts([]);
        setPrintoutsPaginator(null);
      }
    }
  };

  const getBatchDetailsData = async (uniqueId: string) => {
    const data = await getWeaponLicencePrintBatch(uniqueId);
    setBatchDetails(data ?? null);
    return data ?? null;
  };

  const getBatchPrintoutsData = async (
    uniqueId: string,
    currentPage: number = 1,
    perPage: EPerPages = EPerPages.perPage25,
    sort?: ISortOptions | null,
  ) => {
    const data = await searchWeaponLicencePrintBatchPrintouts(
      uniqueId,
      currentPage,
      perPage,
      sort,
    );

    if (data) {
      const { content, ...paginatorData } = data;
      if (content) {
        setBatchPrintouts(content);
        setBatchPrintoutsPaginator(paginatorData);
      } else {
        setBatchPrintouts([]);
        setBatchPrintoutsPaginator(null);
      }
    }
  };

  const getBatchHistoryData = async (
    uniqueId: string,
    currentPage: number = 1,
    perPage: EPerPages = EPerPages.perPage25,
  ) => {
    const data = await getWeaponLicencePrintBatchHistory(uniqueId, currentPage, perPage);

    if (data) {
      const { content, ...paginatorData } = data;
      if (content) {
        setBatchHistory(content);
        setBatchHistoryPaginator(paginatorData);
      } else {
        setBatchHistory([]);
        setBatchHistoryPaginator(null);
      }
    }
  };

  const getPrintoutDetailsData = async (uniqueId: string) => {
    const data = await getWeaponLicencePrint(uniqueId);
    setPrintoutDetails(data ?? null);
    return data ?? null;
  };

  const getBatchesSearchData = async (
    currentPage: number = 1,
    perPage: EPerPages = EPerPages.perPage25,
    query: Partial<IPrintoutsSearchForm> = {},
    sort?: ISortOptions | null,
  ) => {
    const batchesData = await getBatches(currentPage, perPage, sort, query);

    if (batchesData) {
      const { content, ...paginatorData } = batchesData;
      if (content) {
        setBatches(content);
        setBatchesPaginator(paginatorData);
      } else {
        setBatches([]);
        setBatchesPaginator(null);
      }
    }
  };

  const getWeaponLicencePrintHistoryData = async (uniqueId: string) => {
    try {
      const data = await getWeaponLicencePrintHistory(uniqueId);
      if (data) {
        setPrintHistory(data);
      } else {
        setPrintHistory([]);
      }
    } catch {
      setPrintHistory([]);
    }
  };

  const loadDictionaries = async (types: EDictionaryTypes[]) => {
    if (types.includes(EDictionaryTypes.legalType)) {
      getLegalType();
    }
    if (types.includes(EDictionaryTypes.weaponLicencingAuthority)) {
      getWeaponLicencingAuthority();
    }
    if (types.includes(EDictionaryTypes.weaponLicencePrintBatchStatus)) {
      getWeaponLicencePrintBatchStatus();
    }
  };

  return {
    paginator,
    licences,
    getLicencesSearchData,
    loadDictionaries,
    legalType,
    getPrintoutsSearchData,
    printouts,
    printoutsPaginator,
    getBatchesSearchData,
    batches,
    batchesPaginator,
    weaponLicencingAuthority,
    getWeaponLicencingAuthority,
    weaponLicencePrintBatchStatus,
    getWeaponLicencePrintBatchStatus,
    batchDetails,
    getBatchDetailsData,
    batchPrintouts,
    batchPrintoutsPaginator,
    getBatchPrintoutsData,
    batchHistory,
    batchHistoryPaginator,
    getBatchHistoryData,
    printoutDetails,
    getPrintoutDetailsData,
    getWeaponLicencePrintStatus,
    weaponLicencePrintStatus,
    getWeaponLicencePrintHistoryData,
    printHistory,
  };
};
